/**
 * CORE-1653 — участники чата сообщества: кого показать в списке, кого можно
 * упомянуть и чей профиль открыть.
 *
 * Модуль без React и без платформы: общий у панели, поиска и подсказок.
 */
import type { ChatChannelMember } from '../types/auth';
import { buildMentionCandidates, type MentionCandidate } from './chatMentions';
import { chatUserPressTarget, type ChatUserPressTarget } from './chatUserPressTarget';

const USER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Есть ли за id человек с профилем. Карточки событий пишет служебный автор
 * `master-<id>`, ответы ИИ — `ai_*`: страницы профиля у них нет, и нажатие
 * привело бы на «пользователь не найден».
 */
export function isProfileUserId(id: string | null | undefined): boolean {
  return !!id && USER_ID_RE.test(id.replace(/^user_/, ''));
}

/** Куда ведёт нажатие на автора, участника или упоминание. `null` — никуда. */
export function communityProfileTarget(
  tappedUserId: string | null | undefined,
  currentUserId: string | null | undefined,
): ChatUserPressTarget | null {
  if (!isProfileUserId(tappedUserId)) return null;
  return chatUserPressTarget(tappedUserId!.replace(/^user_/, ''), currentUserId);
}

export function isCommunityModerator(member: Pick<ChatChannelMember, 'role'>): boolean {
  return member.role === 'owner' || member.role === 'moderator';
}

/** Живые люди сообщества: без служебных авторов, заблокированных и повторов. */
export function listCommunityMembers(members: readonly ChatChannelMember[]): ChatChannelMember[] {
  const seen = new Set<string>();
  return members
    .filter((m) => {
      if (!isProfileUserId(m.userId) || m.isBanned || !m.name.trim() || seen.has(m.userId)) return false;
      seen.add(m.userId);
      return true;
    })
    .sort((a, b) =>
      Number(isCommunityModerator(b)) - Number(isCommunityModerator(a)) || a.name.localeCompare(b.name, 'ru'));
}

function norm(s: string): string {
  return s.toLocaleLowerCase('ru').replace(/ё/g, 'е').trim();
}

/**
 * Поиск по имени: совпадение в любом месте имени. Совпавшие с началом имени
 * или слова идут первыми — человек чаще набирает имя с начала.
 */
export function searchCommunityMembers(
  members: readonly ChatChannelMember[],
  query: string,
): ChatChannelMember[] {
  const q = norm(query);
  if (!q) return [...members];
  const starts: ChatChannelMember[] = [];
  const contains: ChatChannelMember[] = [];
  members.forEach((m) => {
    const name = norm(m.name);
    if (!name.includes(q)) return;
    (name.split(/\s+/).some((w) => w.startsWith(q)) ? starts : contains).push(m);
  });
  return [...starts, ...contains];
}

/** Кого можно упомянуть в чате сообщества: все участники, кроме себя. */
export function communityMentionCandidates(
  members: readonly ChatChannelMember[],
  selfId: string | null | undefined,
): MentionCandidate[] {
  return buildMentionCandidates(
    listCommunityMembers(members).map((m) => ({
      userId: m.userId,
      name: m.name,
      avatar: m.avatarThumbUrl ?? m.avatarUrl ?? undefined,
      isHost: isCommunityModerator(m),
    })),
    selfId,
  );
}
